import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text
} from "@chakra-ui/react";
import React from "react";
import { FiChevronDown, FiSearch, FiGrid, FiList } from "react-icons/fi";

export type ViewMode = "grid" | "list";

export type HeaderPill = { label: string; value: string; count?: number; icon?: React.ElementType };
export type HeaderSortOption = { label: string; value: string };

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  /** Toggle filter pills (single-select; click active to clear). */
  pills?: { items: HeaderPill[]; value?: string; onChange: (value: string | undefined) => void };
  /** Sort dropdown. */
  sort?: { options: HeaderSortOption[]; value?: string; onChange: (value: string) => void };
  /** Optional search box. */
  search?: { value: string; onChange: (value: string) => void; placeholder?: string };
  /** Optional grid/list view toggle. */
  viewToggle?: { value: ViewMode; onChange: (value: ViewMode) => void };
  /** Primary perspective tabs (np. Autorskie / Biorę udział). */
  tabs?: { items: { label: string; value: string }[]; value: string; onChange: (value: string) => void };
}

const INNER_PX = { base: "20px", md: "40px", xl: "56px" };

export const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, actions, pills, sort, search, viewToggle, tabs }) => {
  const hasControls = !!(pills?.items.length || sort || search || viewToggle);
  const currentSort = sort?.options.find(o => o.value === sort.value);

  return (
    <Box as="section" w="100%">
      <Box maxW="1240px" mx="auto" px={INNER_PX} pt={{ base: "20px", md: "26px" }}>
        <Box
          bg="white"
          border="1px solid rgba(67,0,152,0.08)"
          borderRadius="20px"
          boxShadow="0 6px 20px rgba(67,0,152,0.05)"
          px={{ base: "18px", md: "26px" }}
          py={{ base: "16px", md: "18px" }}
        >
          <Flex justify="space-between" align="center" gap="14px">
          <Box minW={0}>
            <Heading fontSize={{ base: "20px", md: "23px" }} color="primary" fontWeight="800" lineHeight="1.2" letterSpacing="-0.01em" noOfLines={1}>
              {title}
            </Heading>
            {subtitle && (
              <Text color="gray.500" fontSize={{ base: "13px", md: "14px" }} mt="2px" noOfLines={1}>
                {subtitle}
              </Text>
            )}
          </Box>
          {actions && <Box flexShrink={0}>{actions}</Box>}
        </Flex>

        {tabs && (
          <HStack mt="16px" spacing="0" bg="rgba(67,0,152,0.05)" borderRadius="13px" p="4px" w="fit-content" maxW="100%" overflowX="auto">
            {tabs.items.map(t => {
              const active = tabs.value === t.value;
              return (
                <Box
                  as="button"
                  key={t.value}
                  onClick={() => tabs.onChange(t.value)}
                  px={{ base: "16px", md: "22px" }}
                  py="9px"
                  borderRadius="10px"
                  fontSize="14px"
                  fontWeight="700"
                  whiteSpace="nowrap"
                  bg={active ? "white" : "transparent"}
                  color={active ? "primary" : "gray.500"}
                  boxShadow={active ? "0 2px 8px rgba(67,0,152,0.12)" : "none"}
                  transition="all 0.15s"
                  _hover={active ? {} : { color: "primary" }}
                >
                  {t.label}
                </Box>
              );
            })}
          </HStack>
        )}

        {hasControls && (
          <Flex mt="14px" justify="space-between" align={{ base: "stretch", md: "center" }} gap="10px" direction={{ base: "column", md: "row" }} wrap="wrap">
            {pills?.items.length ? (
              <HStack spacing="7px" wrap="wrap" flex="1">
                {pills.items.map(p => {
                  const active = pills.value === p.value;
                  return (
                    <Button
                      key={p.value}
                      h="34px"
                      borderRadius="full"
                      fontWeight="600"
                      fontSize="13px"
                      px="14px"
                      leftIcon={p.icon ? <Icon as={p.icon} boxSize="14px" /> : undefined}
                      iconSpacing="6px"
                      bg={active ? "primary" : "white"}
                      color={active ? "white" : "gray.600"}
                      border="1px solid"
                      borderColor={active ? "primary" : "rgba(67,0,152,0.14)"}
                      _hover={{ bg: active ? "#36007a" : "rgba(67,0,152,0.05)", borderColor: active ? "#36007a" : "rgba(67,0,152,0.25)" }}
                      onClick={() => pills.onChange(active ? undefined : p.value)}
                    >
                      {p.label}
                      {typeof p.count === "number" ? ` ${p.count}` : ""}
                    </Button>
                  );
                })}
              </HStack>
            ) : (
              <Box flex="1" />
            )}

            <HStack spacing="8px" flexShrink={0}>
              {search && (
                <InputGroup w={{ base: "100%", md: "230px" }}>
                  <InputLeftElement h="34px" pointerEvents="none">
                    <FiSearch color="#A0AEC0" size={15} />
                  </InputLeftElement>
                  <Input h="34px" value={search.value} onChange={e => search.onChange(e.target.value)} placeholder={search.placeholder || "Szukaj"} fontSize="14px" bg="white" borderColor="rgba(67,0,152,0.14)" borderRadius="10px" _hover={{ borderColor: "rgba(67,0,152,0.25)" }} _focusVisible={{ borderColor: "primary", boxShadow: "0 0 0 1px var(--chakra-colors-primary)" }} />
                </InputGroup>
              )}
              {sort && (
                <Menu>
                  <MenuButton as={Button} h="34px" bg="white" variant="outline" borderColor="rgba(67,0,152,0.14)" color="gray.600" borderRadius="10px" rightIcon={<FiChevronDown />} fontWeight="600" fontSize="13px" flexShrink={0} _hover={{ bg: "rgba(67,0,152,0.04)", borderColor: "rgba(67,0,152,0.25)" }} _active={{ bg: "rgba(67,0,152,0.06)" }}>
                    {currentSort?.label || "Sortuj"}
                  </MenuButton>
                  <MenuList minW="200px" py="6px">
                    {sort.options.map(o => (
                      <MenuItem key={o.value} onClick={() => sort.onChange(o.value)} fontSize="14px" fontWeight={o.value === sort.value ? "700" : "500"} color={o.value === sort.value ? "primary" : "gray.700"} bg={o.value === sort.value ? "rgba(67,0,152,0.06)" : undefined}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
              {viewToggle && (
                <HStack spacing="0" border="1px solid rgba(67,0,152,0.14)" borderRadius="10px" overflow="hidden" h="34px" flexShrink={0}>
                  {(["list", "grid"] as ViewMode[]).map(mode => {
                    const active = viewToggle.value === mode;
                    return (
                      <IconButton
                        key={mode}
                        aria-label={mode === "grid" ? "Kafelki" : "Lista"}
                        icon={mode === "grid" ? <FiGrid /> : <FiList />}
                        h="34px"
                        minW="38px"
                        borderRadius="0"
                        variant="ghost"
                        bg={active ? "primary" : "white"}
                        color={active ? "white" : "gray.500"}
                        _hover={{ bg: active ? "#36007a" : "rgba(67,0,152,0.05)" }}
                        onClick={() => viewToggle.onChange(mode)}
                      />
                    );
                  })}
                </HStack>
              )}
            </HStack>
          </Flex>
        )}
        </Box>
      </Box>
    </Box>
  );
};
